import PhoneInput from "react-phone-input-2";
import { ReactComponent as Loader } from "../../../assets/loader.svg";
import api from "../../../utils/api";
import { useEffect, useState, useRef } from "react";
import BillingForm from "../utils/BillingForm";

export default function Billing({
  loading,
  handleSubscribe,
  handlePhoneChange,
  isButtonDisabled,
  phoneNumber,
  selectedPlan,
  setSelectedPlan,
  appliedDiscount,
  setAppliedDiscount,
  discountCode,
  setDiscountCode,
  statusMessage,
  darkMode,
  setStatusMessage,
}) {
  const [discounts, setDiscounts] = useState([]);

  useEffect(() => {
    const fetchDiscounts = async () => {
      try {
        const response = await api.get(`/discount/all`);

        const discountsArray = Array.isArray(response)
          ? response
          : response.data;
        setDiscounts(discountsArray);
      } catch (error) {
        console.error("Error fetching discounts:", error);
      }
    };

    fetchDiscounts();
  }, []);

  const handleApply = async () => {
    if (!discountCode) {
      setStatusMessage("Please enter a discount code.");
      setAppliedDiscount(null); // Reset the discount
      return;
    }

    const currentDate = new Date();

    const matchedDiscount = discounts.find(
      (discount) =>
        discount.code === discountCode &&
        discount.is_active &&
        new Date(discount.valid_from) <= currentDate &&
        new Date(discount.valid_to) >= currentDate
    );

    if (!matchedDiscount) {
      setStatusMessage("This discount code is invalid, inactive, or expired.");
      setAppliedDiscount(null);
      return;
    }

    try {
      const response = await api.get(`/discount/${discountCode}/details`);
      setStatusMessage(
        `Discount applied successfully! You saved ${matchedDiscount.value}%`
      );
      setAppliedDiscount(matchedDiscount);
    } catch (error) {
      console.error("Error applying discount:", error);
      setStatusMessage("Error applying discount. Please try again.");
      setAppliedDiscount(null);
    }
  };

  const planDetails =
    selectedPlan === "monthly"
      ? { price: 16.99, label: "BETH Premium - Monthly", term: "per month" }
      : { price: 203.88, label: "BETH Premium - Annually", term: "per year" };

  // const discountedPrice =
  //   selectedPlan === "annual"
  //     ? planDetails.price * (1 - 0.2)
  //     : planDetails.price;

  // const discountEarly = 2.0;
  return (
    <form
      onSubmit={handleSubscribe}
      className="p-8 mt-6 flex flex-col items-center">
      <div className="flex flex-col md:flex-row gap-10 w-full">
        {/* Left Side */}
        <div className="flex flex-col gap-4 w-full">
          <div className="text-left">
            <h2 className="text-2xl font-bold">Subscribe to BETH Premium</h2>
            <p className="mt-1 text-gray-500 dark:text-gray-400">
              {selectedPlan === "monthly"
                ? "Billed monthly"
                : "Billed annually"}
            </p>
          </div>
          {/* Plan Selection */}
          <div className="bg-slate-200 flex gap-2 items-center flex-col dark:bg-slate-700 p-4 rounded-lg">
            <h3 className="text-lg font-bold">Choose Your Plan</h3>
            <div className="flex justify-center gap-4">
              <button
                type="button"
                className={`px-4 py-2 rounded-lg ${
                  selectedPlan === "monthly"
                    ? "bg-green-500 font-bold"
                    : "border-slate-400 dark:border-slate-500 border-2"
                }`}
                onClick={() => setSelectedPlan("monthly")}>
                Monthly
              </button>
              <button
                type="button"
                className={`px-4 py-2 rounded-lg ${
                  selectedPlan === "annual"
                    ? "bg-green-500 font-bold"
                    : "border-slate-400 dark:border-slate-500 border-2"
                }`}
                onClick={() => setSelectedPlan("annual")}>
                Annual
              </button>
            </div>
          </div>

          <div className="md:flex flex-col mt-2 hidden gap-2">
            <h3 className="text-lg mb-2 font-bold">Billing Details</h3>
            {/* Plan Details */}
            <div className="flex justify-between">
              <p className="">{planDetails.label}</p>
              <p className="font-semibold">${planDetails.price.toFixed(2)}</p>
            </div>
            {/* Discount Section */}
            {appliedDiscount ? (
              <div className="flex justify-between">
                <div className="flex flex-col">
                  <p className="">Discount</p>
                  {appliedDiscount.code && (
                    <p className="italic ">-{appliedDiscount.code}</p>
                  )}
                </div>
                <p className="font-semibold">
                  - $
                  {((planDetails.price * appliedDiscount.value) / 100).toFixed(
                    2
                  )}
                </p>
              </div>
            ) : (
              <div className="flex justify-between">
                <p className="">Discount</p>
                <p className="font-semibold">$0.00</p>
              </div>
            )}
            <div className="flex justify-between py-2 border-t-2 border-slate-300 pt-2 dark:border-gray-600">
              <p className="text-lg font-bold text-green-700 dark:text-green-400">
                Total Due
              </p>
              <p className="text-lg font-bold text-green-700 dark:text-green-400">
                $
                {(
                  planDetails.price -
                  (appliedDiscount
                    ? (planDetails.price * appliedDiscount.value) / 100
                    : 0)
                ).toFixed(2)}
              </p>
            </div>
          </div>
          <button
            type="submit"
            disabled={isButtonDisabled}
            className={`max-w-xl py-2 px-4 mt-1 font-bold w-full rounded-lg bg-green-500 hover:bg-green-600 transition-colors md:block hidden ${
              isButtonDisabled ? "opacity-50 cursor-not-allowed" : ""
            }`}>
            {loading ? (
              <Loader
                id="spinner"
                className="h-6 w-6 mx-auto"
              />
            ) : (
              "Subscribe"
            )}
          </button>
        </div>
        {/* Right Side */}
        <div className="flex flex-col gap-4 w-full">
          <div className="">
            <h3 className="text-lg font-bold">Billing Information</h3>
            <BillingForm
              phoneNumber={phoneNumber}
              handlePhoneChange={handlePhoneChange}
            />
            <div className="flex flex-col mt-3 gap-2">
              <label
                htmlFor="discountCode"
                className="font-semibold">
                Discount Code
              </label>
              <div className="flex flex-wrap gap-2 w-full justify-center">
                <input
                  type="text"
                  id="discountCode"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value)}
                  className="flex-grow py-2 px-4 border border-slate-300 rounded-lg  placeholder-gray-400 dark:border-slate-600 focus:ring-2 dark:bg-slate-700 focus:ring-green-500 dark:focus:ring-green-500 focus:outline-none"
                  placeholder="Enter discount code"
                />
                <button
                  type="button"
                  onClick={handleApply}
                  className="px-4 py-2 font-semibold text-gray-100 bg-green-500 rounded-lg hover:bg-green-600">
                  Apply
                </button>
              </div>
            </div>
            {statusMessage && (
              <p
                className={`mt-2 text-sm ${
                  statusMessage.includes("success")
                    ? "text-green-500"
                    : "text-red-500"
                }`}>
                {statusMessage}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col md:hidden gap-2">
          <div className="flex flex-col gap-2">
            <h3 className="text-lg mb-2 font-bold">Billing Details</h3>
            {/* Plan Details */}
            <div className="flex justify-between">
              <p className="">{planDetails.label}</p>
              <p className="font-semibold">${planDetails.price.toFixed(2)}</p>
            </div>
            {/* Discount Section */}
            {appliedDiscount ? (
              <div className="flex justify-between">
                <div className="flex flex-col">
                  <p className="">Discount</p>
                  {appliedDiscount.code && (
                    <p className="italic ">-{appliedDiscount.code}</p>
                  )}
                </div>
                <p className="font-semibold">
                  - $
                  {((planDetails.price * appliedDiscount.value) / 100).toFixed(
                    2
                  )}
                </p>
              </div>
            ) : (
              <div className="flex justify-between">
                <p className="">Discount</p>
                <p className="font-semibold">$0.00</p>
              </div>
            )}
            <div className="flex justify-between py-2 border-t-2 border-slate-300 pt-2 dark:border-gray-600">
              <p className="text-lg font-bold text-green-700 dark:text-green-400">
                Total Due
              </p>
              <p className="text-lg font-bold text-green-700 dark:text-green-400">
                $
                {(
                  planDetails.price -
                  (appliedDiscount
                    ? (planDetails.price * appliedDiscount.value) / 100
                    : 0)
                ).toFixed(2)}
              </p>
            </div>
          </div>
          <button
            type="submit"
            disabled={isButtonDisabled}
            className={`max-w-xl py-2 px-4 font-bold w-full rounded-lg bg-green-500 hover:bg-green-600 transition-colors ${
              isButtonDisabled ? "opacity-50 cursor-not-allowed" : ""
            }`}>
            {loading ? (
              <Loader
                id="spinner"
                className="h-6 w-6 mx-auto"
              />
            ) : (
              "Subscribe"
            )}
          </button>
        </div>
      </div>
    </form>
  );
}
